import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  House, MapPin, Clock, CurrencyEur, Sparkle, ArrowRight, ArrowLeft, CheckCircle,
  Storefront, Funnel, Star, Users, ChartBar, Phone, EnvelopeSimple, Trophy,
} from "@phosphor-icons/react";

/**
 * Marketplace — feed agenti (Mossa 2 · Asta Inversa).
 *
 * 3 tab interni: Feed (listings attivi) · I miei pitch · Statistiche.
 * - Feed: filtri per città. Click su card → drawer con form pitch.
 * - I miei pitch: dashboard delle proposte inviate con badge "Selezionato".
 * - Stats: KPI rapidi (sent, selected, this month).
 *
 * Privacy: la card mostra solo zona macro + caratteristiche + foto del listing.
 * I contatti del proprietario appaiono solo se l'agente è stato selezionato.
 */
export default function Marketplace() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("feed");
  const [city, setCity] = useState("");
  const [minSqm, setMinSqm] = useState("");
  const [maxSqm, setMaxSqm] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [timing, setTiming] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [feed, setFeed] = useState([]);
  const [pitches, setPitches] = useState([]);
  const [stats, setStats] = useState(null);
  const [refLink, setRefLink] = useState(null); // { slug, ref_url_path, referred_total, referred_active }
  const [leaderboard, setLeaderboard] = useState(null); // { city, position, total, me, top3 }
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // active listing for pitch

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (city) params.city = city;
      if (minSqm) params.min_sqm = parseInt(minSqm, 10);
      if (maxSqm) params.max_sqm = parseInt(maxSqm, 10);
      if (maxPrice) params.max_price = parseInt(maxPrice, 10);
      if (timing) params.timing = timing;
      if (onlyOpen) params.only_open = true;
      const [f, m, s, r, lb] = await Promise.all([
        api.get("/marketplace/feed", { params }),
        api.get("/marketplace/my-pitches"),
        api.get("/marketplace/stats"),
        api.get("/marketplace/my-ref-link").catch(() => ({ data: null })),
        api.get("/marketplace/my-leaderboard-position").catch(() => ({ data: null })),
      ]);
      setFeed(f.data.items || []);
      setPitches(m.data.items || []);
      setStats(s.data);
      setRefLink(r?.data || null);
      setLeaderboard(lb?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di caricamento.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [city, minSqm, maxSqm, maxPrice, timing, onlyOpen]);

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1C1C1A]" data-testid="marketplace-page">
      <header className="sticky top-0 z-30 bg-[#FAF9F5]/90 backdrop-blur border-b border-[#EAE7DF]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-14 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-sm text-[#3F3D38] hover:text-[#1C1C1A] inline-flex items-center gap-1" data-testid="marketplace-back">
            <ArrowLeft size={14}/> Dashboard
          </button>
          <div className="flex items-center gap-1.5">
            <Storefront size={16} className="text-[#7C8B6E]" weight="duotone"/>
            <h1 className="font-editorial text-lg">Marketplace</h1>
          </div>
          <div className="w-[80px]"/>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 pt-6 pb-20">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 bg-white border border-[#EAE7DF] rounded-full p-1 max-w-fit">
          <TabBtn id="feed"  active={tab} onClick={setTab} icon={Funnel} label="Annunci" testid="mkt-tab-feed"/>
          <TabBtn id="mine"  active={tab} onClick={setTab} icon={Sparkle} label="Le mie proposte" badge={pitches.length} testid="mkt-tab-mine"/>
          <TabBtn id="stats" active={tab} onClick={setTab} icon={ChartBar} label="Statistiche" testid="mkt-tab-stats"/>
        </div>

        {tab === "feed" && (
          <FeedView
            feed={feed} loading={loading}
            city={city} setCity={setCity}
            minSqm={minSqm} setMinSqm={setMinSqm}
            maxSqm={maxSqm} setMaxSqm={setMaxSqm}
            maxPrice={maxPrice} setMaxPrice={setMaxPrice}
            timing={timing} setTiming={setTiming}
            onlyOpen={onlyOpen} setOnlyOpen={setOnlyOpen}
            onOpen={setDrawer}
          />
        )}

        {tab === "mine" && (
          <MineView pitches={pitches} loading={loading}/>
        )}

        {tab === "stats" && (
          <StatsView stats={stats} refLink={refLink} leaderboard={leaderboard}/>
        )}
      </main>

      {drawer && (
        <PitchDrawer
          listing={drawer}
          onClose={() => setDrawer(null)}
          onSent={() => { setDrawer(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ id, active, onClick, icon: Icon, label, badge, testid }) {
  const isOn = active === id;
  return (
    <button
      onClick={() => onClick(id)}
      className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${
        isOn ? "bg-[#1C1C1A] text-white" : "text-[#3F3D38] hover:text-[#1C1C1A]"
      }`}
      data-testid={testid}
    >
      <Icon size={13} weight={isOn ? "fill" : "regular"}/> {label}
      {badge ? <span className={`ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${isOn ? "bg-white/20" : "bg-[#F1F4ED] text-[#5A6A4D]"}`}>{badge}</span> : null}
    </button>
  );
}

function FeedView({ feed, loading, city, setCity, minSqm, setMinSqm, maxSqm, setMaxSqm, maxPrice, setMaxPrice, timing, setTiming, onlyOpen, setOnlyOpen, onOpen }) {
  const anyFilter = city || minSqm || maxSqm || maxPrice || timing || onlyOpen;
  const reset = () => { setCity(""); setMinSqm(""); setMaxSqm(""); setMaxPrice(""); setTiming(""); setOnlyOpen(false); };
  return (
    <>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h2 className="font-editorial text-2xl sm:text-3xl flex-1 min-w-[200px]">Proprietari che cercano un agente</h2>
        {anyFilter && (
          <button onClick={reset} className="text-[11.5px] text-[#7C8B6E] underline hover:text-[#1C1C1A]" data-testid="mkt-filters-reset">Rimuovi filtri</button>
        )}
      </div>
      <p className="text-[12.5px] text-[#5A584F] mb-4 max-w-xl">
        Hanno già deciso di vendere con un agente. Mandagli la proposta migliore.
      </p>

      <div className="bg-white border border-[#EAE7DF] rounded-xl p-3 mb-5 flex items-center gap-2 flex-wrap" data-testid="mkt-filters">
        <FilterField icon={MapPin} label="Città">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Milano" className={filterInp + " w-[140px]"} data-testid="mkt-city-filter"/>
        </FilterField>
        <FilterField label="Mq min">
          <input type="number" value={minSqm} onChange={(e) => setMinSqm(e.target.value)} placeholder="60" className={filterInp + " w-[70px]"} data-testid="mkt-min-sqm"/>
        </FilterField>
        <FilterField label="Mq max">
          <input type="number" value={maxSqm} onChange={(e) => setMaxSqm(e.target.value)} placeholder="200" className={filterInp + " w-[70px]"} data-testid="mkt-max-sqm"/>
        </FilterField>
        <FilterField label="Prezzo max">
          <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="500000" className={filterInp + " w-[100px]"} data-testid="mkt-max-price"/>
        </FilterField>
        <FilterField label="Tempistica">
          <select value={timing} onChange={(e) => setTiming(e.target.value)} className={filterInp + " w-[120px]"} data-testid="mkt-timing">
            <option value="">tutte</option>
            <option value="subito">Subito</option>
            <option value="1-3-mesi">1-3 mesi</option>
            <option value="3-6-mesi">3-6 mesi</option>
            <option value="6-12-mesi">6-12 mesi</option>
            <option value="esplorativo">Esplorativo</option>
          </select>
        </FilterField>
        <label className="flex items-center gap-1.5 text-[12px] text-[#3F3D38] ml-auto cursor-pointer">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} data-testid="mkt-only-open"/>
          Solo dove non ho ancora proposto
        </label>
      </div>

      {loading ? <Loading/> : feed.length === 0 ? <EmptyState text={anyFilter ? "Nessun annuncio con questi filtri." : "Nessun annuncio attivo per ora."}/> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {feed.map((l) => <ListingCard key={l.id} listing={l} onOpen={() => onOpen(l)}/>)}
        </div>
      )}
    </>
  );
}

function FilterField({ icon: Icon, label, children }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#5A584F]">
      {Icon ? <Icon size={12} className="text-[#7C8B6E]"/> : null}
      <span className="uppercase tracking-wider text-[9.5px]">{label}</span>
      {children}
    </label>
  );
}

function ListingCard({ listing, onOpen }) {
  const hasPitched = !!listing.my_pitch_id;
  const isMyRef = !!listing.is_my_referral;
  return (
    <div className={`bg-white border ${isMyRef ? "border-[#7C8B6E] ring-2 ring-[#7C8B6E]/25" : "border-[#EAE7DF]"} rounded-2xl overflow-hidden hover:border-[#7C8B6E] transition-colors flex flex-col`} data-testid={`mkt-listing-${listing.id}`}>
      <div className="aspect-[16/9] bg-[#F1F4ED] relative overflow-hidden">
        {listing.photos?.[0] ? (
          <img src={listing.photos[0]} alt="" className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#9A968B]"><House size={36} weight="duotone"/></div>
        )}
        <div className="absolute top-2 left-2 text-[9.5px] uppercase tracking-widest bg-white/95 px-2 py-1 rounded font-bold text-[#7C8B6E]">
          {listing.city}{listing.area ? ` · ${listing.area}` : ""}
        </div>
        {isMyRef && !hasPitched && (
          <div className="absolute top-2 right-2 text-[9.5px] uppercase tracking-widest bg-[#7C8B6E] text-white px-2 py-1 rounded font-bold shadow-sm" data-testid={`mkt-myref-${listing.id}`}>
            ⭐ Tuo contatto
          </div>
        )}
        {hasPitched && (
          <div className={`absolute top-2 right-2 text-[9.5px] uppercase tracking-widest px-2 py-1 rounded font-bold ${
            listing.my_pitch_selected ? "bg-[#7C8B6E] text-white" : "bg-[#1C1C1A] text-white"
          }`}>
            {listing.my_pitch_selected ? "✓ Scelto" : "Hai proposto"}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {isMyRef && (
          <div className="text-[10.5px] font-semibold text-[#5A6A4D] bg-[#F1F4ED] border border-[#7C8B6E]/30 px-2 py-1 rounded-md mb-2 inline-block self-start">
            Arrivato dal tuo link personale
          </div>
        )}
        <div className="flex items-center gap-2.5 text-[12.5px] text-[#3F3D38] mb-1.5">
          <span><strong className="text-[#1C1C1A]">{listing.surface_sqm}</strong>mq</span>
          <span className="text-[#D4CFC4]">·</span>
          <span><strong className="text-[#1C1C1A]">{listing.rooms}</strong> loc</span>
          {listing.bathrooms ? <><span className="text-[#D4CFC4]">·</span><span><strong className="text-[#1C1C1A]">{listing.bathrooms}</strong> bagni</span></> : null}
        </div>
        {listing.target_price && (
          <div className="text-[14px] text-[#1C1C1A] mb-1.5">Obiettivo <strong>€ {listing.target_price.toLocaleString("it-IT")}</strong></div>
        )}
        {listing.motivation && <div className="text-[11.5px] text-[#5A584F] italic mb-2.5 line-clamp-2">"{listing.motivation}"</div>}
        <div className="flex items-center gap-3 text-[11px] text-[#9A968B] mb-3">
          <span className="inline-flex items-center gap-1"><Clock size={11}/> {formatTiming(listing.timing)}</span>
          <span className="inline-flex items-center gap-1"><Users size={11}/> {listing.pitch_count === 0 ? "Nessuna proposta" : (listing.pitch_count === 1 ? "1 proposta" : `${listing.pitch_count} proposte`)}</span>
          {typeof listing.days_left === "number" && listing.days_left <= 3 && (
            <span className={`inline-flex items-center gap-1 ml-auto px-1.5 py-0.5 rounded font-semibold text-[10px] ${listing.days_left === 0 ? "bg-[#FFE5E5] text-[#A12C2C]" : "bg-[#FFF3D6] text-[#9C7A1A]"}`}>
              {listing.days_left === 0 ? "Scade oggi" : `Scade tra ${listing.days_left}g`}
            </span>
          )}
        </div>
        <button
          onClick={onOpen}
          disabled={hasPitched}
          className={`mt-auto w-full px-3 py-2 rounded-md text-[13px] font-semibold transition-colors ${
            hasPitched ? "bg-[#F1F4ED] text-[#9A968B] cursor-not-allowed" : "bg-[#1C1C1A] text-white hover:bg-[#000]"
          }`}
          data-testid={`mkt-pitch-btn-${listing.id}`}
        >
          {hasPitched ? "Hai già proposto" : <>Manda proposta <ArrowRight size={14} weight="bold" className="inline ml-1"/></>}
        </button>
      </div>
    </div>
  );
}

function PitchDrawer({ listing, onClose, onSent }) {
  const [form, setForm] = useState({
    proposed_commission_pct: "3",
    listing_price_estimate: listing.target_price ? String(listing.target_price) : "",
    time_to_sale_weeks: "12",
    plan_1: "",
    plan_2: "",
    plan_3: "",
    pitch_text: "",
  });
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const send = async (e) => {
    e.preventDefault();
    const plan = [form.plan_1, form.plan_2, form.plan_3].filter((x) => x.trim());
    if (!plan.length) { toast.error("Aggiungi almeno 1 punto del piano marketing."); return; }
    if (form.pitch_text.trim().length < 20) { toast.error("Il testo deve avere almeno 20 caratteri."); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/listings/${listing.id}/pitch`, {
        proposed_commission_pct: parseFloat(form.proposed_commission_pct),
        listing_price_estimate: parseInt(form.listing_price_estimate, 10),
        time_to_sale_weeks: parseInt(form.time_to_sale_weeks, 10),
        marketing_plan: plan,
        pitch_text: form.pitch_text.trim(),
      });
      toast.success("Proposta inviata!");
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-end" onClick={onClose} data-testid="mkt-pitch-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FAF9F5] w-full sm:max-w-md sm:h-full overflow-y-auto rounded-t-2xl sm:rounded-none">
        <div className="sticky top-0 bg-[#FAF9F5] border-b border-[#EAE7DF] px-5 py-3 flex items-center justify-between">
          <h3 className="font-editorial text-lg">La tua proposta</h3>
          <button onClick={onClose} className="text-[#9A968B] hover:text-[#1C1C1A]" data-testid="mkt-pitch-close">✕</button>
        </div>

        <div className="px-5 py-4 border-b border-[#EAE7DF] bg-white">
          <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold">{listing.city}{listing.area ? ` · ${listing.area}` : ""}</div>
          <div className="text-[14px] mt-0.5"><strong>{listing.surface_sqm}</strong>mq · <strong>{listing.rooms}</strong> loc {listing.target_price ? <span className="text-[#5A584F]">· obiettivo € {listing.target_price.toLocaleString("it-IT")}</span> : null}</div>
        </div>

        <form onSubmit={send} className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Comm. %">
              <input type="number" step="0.1" value={form.proposed_commission_pct} onChange={(e) => setF("proposed_commission_pct", e.target.value)} className={inp} required data-testid="mkt-pitch-commission"/>
            </Field>
            <Field label="Listino €">
              <input type="number" value={form.listing_price_estimate} onChange={(e) => setF("listing_price_estimate", e.target.value)} className={inp} required data-testid="mkt-pitch-price"/>
            </Field>
            <Field label="Settimane">
              <input type="number" value={form.time_to_sale_weeks} onChange={(e) => setF("time_to_sale_weeks", e.target.value)} className={inp} required data-testid="mkt-pitch-weeks"/>
            </Field>
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold text-[#3F3D38] mb-1.5">Piano marketing (1-3 punti)</label>
            <div className="space-y-2">
              <input value={form.plan_1} onChange={(e) => setF("plan_1", e.target.value)} placeholder="Es. Servizio foto professionale + virtual staging" className={inp} data-testid="mkt-pitch-plan-1"/>
              <input value={form.plan_2} onChange={(e) => setF("plan_2", e.target.value)} placeholder="Es. Annuncio su Idealista + Immobiliare.it" className={inp} data-testid="mkt-pitch-plan-2"/>
              <input value={form.plan_3} onChange={(e) => setF("plan_3", e.target.value)} placeholder="Es. Open house weekend + campagna social" className={inp} data-testid="mkt-pitch-plan-3"/>
            </div>
          </div>

          <Field label="Perché tu (20-600 caratteri)">
            <textarea value={form.pitch_text} onChange={(e) => setF("pitch_text", e.target.value)} placeholder="Sono attivo a Brera da X anni, conosco bene il mercato, ho Y acquirenti qualificati..." className={inp + " min-h-[120px]"} maxLength="600" required data-testid="mkt-pitch-text"/>
          </Field>

          <button type="submit" disabled={busy} className="w-full px-4 py-3 rounded-md bg-[#1C1C1A] text-white font-semibold text-[14px] hover:bg-[#000] disabled:opacity-60" data-testid="mkt-pitch-send">
            {busy ? "Invio…" : "Invia proposta"}
          </button>
          <p className="text-[10.5px] text-[#9A968B] text-center">Il proprietario vedrà solo il tuo nome e questa proposta. I tuoi dati personali restano invisibili finché non ti sceglie.</p>
        </form>
      </div>
    </div>
  );
}

function MineView({ pitches, loading }) {
  if (loading) return <Loading/>;
  if (pitches.length === 0) return <EmptyState text="Non hai ancora inviato proposte. Vai al Feed."/>;
  return (
    <div className="space-y-3">
      {pitches.map((p) => (
        <div key={p.id} className="bg-white border border-[#EAE7DF] rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`mkt-mypitch-${p.id}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#7C8B6E] font-bold mb-0.5">{p.listing_city}{p.listing_area ? ` · ${p.listing_area}` : ""}</div>
            <div className="text-[14px]"><strong>{p.listing_surface_sqm}</strong>mq · <strong>{p.listing_rooms}</strong> loc · € {p.listing_price_estimate.toLocaleString("it-IT")} · {p.proposed_commission_pct}%</div>
            <div className="text-[11px] text-[#9A968B] mt-0.5">{new Date(p.created_at).toLocaleString("it-IT")}</div>
          </div>
          {p.selected ? (
            <div className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full bg-[#7C8B6E] text-white font-bold">
              <CheckCircle size={12} weight="fill"/> Scelto · contatti sbloccati
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-full bg-[#F1F4ED] text-[#5A6A4D] font-semibold">In attesa</div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatsView({ stats, refLink, leaderboard }) {
  if (!stats) return <Loading/>;
  return (
    <div className="space-y-4">
      {refLink && <MyRefLinkCard refLink={refLink}/>}
      {leaderboard && <LeaderboardCard data={leaderboard}/>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Proposte inviate" value={stats.sent_total} icon={Sparkle}/>
        <Stat label="Proposte scelte" value={stats.selected_total} icon={CheckCircle}/>
        <Stat label="Tasso di scelta" value={`${stats.conversion_rate_pct ?? 0}%`} icon={Trophy} hint={stats.sent_total > 0 ? `${stats.selected_total} su ${stats.sent_total}` : "Manda la prima proposta"}/>
        <Stat label="Questo mese" value={stats.this_month_used} icon={ChartBar}/>
      </div>
      {(stats.avg_commission_zone_pct || stats.avg_price_zone) && (
        <div className="bg-white border border-[#EAE7DF] rounded-2xl p-5">
          <h3 className="font-editorial text-lg mb-1">Benchmark zona {stats.city ? `· ${stats.city}` : ""}</h3>
          <p className="text-[12px] text-[#5A584F] mb-3">Media delle proposte degli altri agenti nella tua città. Usa questi numeri per posizionare la tua proposta.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {stats.avg_commission_zone_pct != null && (
              <div className="bg-[#F8F7F2] rounded-md px-3 py-2 border border-[#EAE7DF]">
                <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold">Commissione media</div>
                <div className="font-editorial text-2xl text-[#1C1C1A] tabular-nums">{stats.avg_commission_zone_pct}%</div>
              </div>
            )}
            {stats.avg_price_zone != null && (
              <div className="bg-[#F8F7F2] rounded-md px-3 py-2 border border-[#EAE7DF]">
                <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold">Prezzo listino medio</div>
                <div className="font-editorial text-2xl text-[#1C1C1A] tabular-nums">€ {stats.avg_price_zone.toLocaleString("it-IT")}</div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="text-[11.5px] text-[#9A968B] italic">{stats.active_listings_in_my_city} {stats.active_listings_in_my_city === 1 ? "annuncio attivo" : "annunci attivi"} nella tua città.</div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, hint }) {
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold flex items-center gap-1 mb-1.5"><Icon size={11} weight="fill"/> {label}</div>
      <div className="font-editorial text-3xl text-[#1C1C1A] tabular-nums">{value ?? 0}</div>
      {hint && <div className="text-[10.5px] text-[#9A968B] mt-0.5">{hint}</div>}
    </div>
  );
}

function MyRefLinkCard({ refLink }) {
  const fullUrl = `${window.location.origin}${refLink.ref_url_path}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copiato. Mettilo su WhatsApp, biglietti, IG.");
    } catch {
      toast.success(`Link: ${fullUrl}`);
    }
  };
  const waMsg = encodeURIComponent(
    `Ciao! Se conosci qualcuno che vuole vendere casa, può caricarla qui in 2 minuti e riceverà la mia proposta di mandato: ${fullUrl}`,
  );
  return (
    <div className="bg-[#1C1C1A] text-white rounded-2xl p-5" data-testid="mkt-my-ref-link">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A8B89A] font-bold mb-1">
            ⭐ Il tuo link Marketplace
          </div>
          <h3 className="font-editorial text-xl leading-tight">
            Trova proprietari prima degli altri agenti.
          </h3>
        </div>
      </div>

      <div className="bg-white/10 border border-white/15 rounded-md px-3 py-2.5 mb-3 font-mono text-[12px] sm:text-[13px] text-white break-all select-all" data-testid="mkt-ref-url">
        {fullUrl}
      </div>

      <p className="text-[11.5px] text-[#D6D3CB] leading-snug mb-3">
        Mettilo su WhatsApp, Instagram, biglietti. Chi lo usa arriva <strong className="text-white">solo da te</strong>: notifica subito, badge "Tuo contatto", priorità nell'elenco.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={copy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white text-[#1C1C1A] text-[12.5px] font-semibold hover:bg-[#F1F4ED]"
          data-testid="mkt-ref-copy"
        >
          Copia link
        </button>
        <a
          href={`https://wa.me/?text=${waMsg}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#25D366] text-white text-[12.5px] font-semibold hover:bg-[#20BA5A]"
          data-testid="mkt-ref-whatsapp"
        >
          Condividi su WhatsApp
        </a>
        <span className="ml-auto text-[11px] text-[#A8B89A] inline-flex items-center gap-2">
          <span><strong className="text-white">{refLink.referred_total ?? 0}</strong> {refLink.referred_total === 1 ? "contatto" : "contatti"}</span>
          {refLink.referred_active > 0 && (
            <>
              <span className="opacity-50">·</span>
              <span><strong className="text-white">{refLink.referred_active}</strong> attivi</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function Loading() { return <div className="text-center py-16 text-[#9A968B] text-[13px]">Carico…</div>; }
function EmptyState({ text }) {
  return (
    <div className="text-center py-16 bg-white border border-[#EAE7DF] rounded-2xl text-[#5A584F]">
      <Storefront size={36} weight="duotone" className="mx-auto text-[#7C8B6E] mb-3 opacity-70"/>
      <div className="text-[14px] font-semibold text-[#1C1C1A] mb-1">{text}</div>
      <p className="text-[12px] text-[#9A968B] max-w-sm mx-auto">Nel frattempo, condividi il tuo link Macchina Mandati: porta clienti senza concorrenza.</p>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-md border border-[#EAE7DF] bg-white text-[13.5px] text-[#1C1C1A] placeholder:text-[#9A968B] focus:outline-none focus:ring-2 focus:ring-[#7C8B6E]/40 focus:border-[#7C8B6E]";
const filterInp = "px-2 py-1 rounded border border-[#EAE7DF] bg-white text-[12px] text-[#1C1C1A] placeholder:text-[#9A968B] focus:outline-none focus:ring-1 focus:ring-[#7C8B6E]/40 focus:border-[#7C8B6E]";

// Map listing.timing key → user-friendly Italian label.
const TIMING_LABEL = {
  "subito": "Vuole vendere subito",
  "1-3-mesi": "Entro 3 mesi",
  "3-6-mesi": "Entro 6 mesi",
  "6-12-mesi": "Entro un anno",
  "esplorativo": "Sta esplorando",
};
const formatTiming = (t) => TIMING_LABEL[t] || "Tempistica da definire";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold text-[#3F3D38] mb-1">{label}</label>
      {children}
    </div>
  );
}

function LeaderboardCard({ data }) {
  const { city, position, total, me, top3 } = data;
  if (!top3 || top3.length === 0) return null;
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-5" data-testid="mkt-leaderboard">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold mb-0.5">
            Classifica agenti {city ? `· ${city}` : ""}
          </div>
          <h3 className="font-editorial text-lg leading-tight">
            {position ? (
              <>
                Sei <span className="text-[#7C8B6E]">{position}°</span> su {total}
              </>
            ) : (
              <>Top agenti della tua città</>
            )}
          </h3>
        </div>
        {position && position <= 3 && (
          <div className="text-[10.5px] uppercase tracking-widest font-bold bg-[#D4A93B] text-[#1C1C1A] px-2 py-1 rounded">
            {position === 1 ? "🥇 Primo" : position === 2 ? "🥈 Secondo" : "🥉 Terzo"}
          </div>
        )}
      </div>

      <ul className="space-y-1.5" data-testid="mkt-leaderboard-list">
        {top3.map((a, idx) => {
          const isMe = me && a.agent_id === me.agent_id;
          return (
            <li
              key={a.agent_id}
              className={`flex items-center gap-3 px-3 py-2 rounded-md border ${
                isMe ? "bg-[#F1F4ED] border-[#7C8B6E]" : "bg-[#FAFAF7] border-[#EAE7DF]"
              }`}
              data-testid={`mkt-leaderboard-row-${idx + 1}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${isMe ? "bg-[#7C8B6E] text-white" : "bg-white border border-[#EAE7DF] text-[#5A6A4D]"}`}>
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1C1C1A] truncate">
                  {a.name}{isMe ? <span className="text-[#7C8B6E] ml-1">· tu</span> : null}
                </div>
                <div className="text-[10.5px] text-[#5A584F] flex items-center gap-2 flex-wrap">
                  <span>{a.selected_count} {a.selected_count === 1 ? "mandato" : "mandati"}</span>
                  {a.reviews_count > 0 && a.avg_rating != null && (
                    <>
                      <span className="text-[#D6D3CB]">·</span>
                      <span className="inline-flex items-center gap-0.5">
                        <Star size={10} weight="fill" className="text-[#D4A93B]"/>
                        {a.avg_rating.toFixed(1)} ({a.reviews_count})
                      </span>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {position && position > 3 && me && (
        <div className="mt-2 flex items-center gap-3 px-3 py-2 rounded-md border-2 border-dashed border-[#7C8B6E]/40 bg-[#FAF9F5]" data-testid="mkt-leaderboard-me">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold bg-[#7C8B6E] text-white">{position}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1C1C1A] truncate">
              {me.name} <span className="text-[#7C8B6E]">· tu</span>
            </div>
            <div className="text-[10.5px] text-[#5A584F] flex items-center gap-2 flex-wrap">
              <span>{me.selected_count} {me.selected_count === 1 ? "mandato" : "mandati"}</span>
              {me.reviews_count > 0 && me.avg_rating != null && (
                <>
                  <span className="text-[#D6D3CB]">·</span>
                  <span className="inline-flex items-center gap-0.5">
                    <Star size={10} weight="fill" className="text-[#D4A93B]"/>
                    {me.avg_rating.toFixed(1)} ({me.reviews_count})
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="mt-3 text-[10.5px] text-[#9A968B] italic">
        Classifica calcolata su mandati conquistati + recensioni dei proprietari. Più sei scelto, più sali.
      </p>
    </div>
  );
}
